import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Star,
  AlertCircle,
  TrendingUp,
  Maximize2,
  CheckCircle2,
  Lightbulb,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';

interface NarrativePromptsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  /** A snapshot past Draft cannot be written to — see the same prop on `DecisionOutputsPanel`. */
  readOnly?: boolean;
  onPromptsUpdate: (prompts: Record<string, string>) => void;
}

interface PromptConfig {
  id: string;
  questionKey: string;
  Icon: typeof Star;
  color: string;
  placeholderKey: string;
}

/**
 * The four questions, by stored id. Exported because the employee's read-only view (CO-437) has to
 * label the same answers, and a second copy of this map would drift the moment a question changes.
 */
export const NARRATIVE_PROMPT_QUESTION_KEYS: Record<string, string> = {
  impactMatters: 'performanceSnapshot.prompts.impactMatters.question',
  limitedImpact: 'performanceSnapshot.prompts.limitedImpact.question',
  doMoreLess: 'performanceSnapshot.prompts.doMoreLess.question',
  scopeExpansion: 'performanceSnapshot.prompts.scopeExpansion.question',
};

const EMPTY_RESPONSES: Record<string, string> = {
  impactMatters: '',
  limitedImpact: '',
  doMoreLess: '',
  scopeExpansion: '',
};

const NarrativePromptsPanel = ({
  reviewData,
  readOnly = false,
  onPromptsUpdate,
}: NarrativePromptsPanelProps) => {
  const { t } = useTranslation();

  /**
   * CO-432 — seeded from `reviewData.prompts`, the LIVE page state, not from the saved server copy.
   * The page used to remount this panel on every tab switch; now it does not, but seeding from the
   * server copy would still discard unsaved answers the first time the panel is re-created for any
   * other reason (a new snapshot, a route re-entry).
   */
  const [responses, setResponses] = useState<Record<string, string>>(
    () => reviewData.prompts ?? { ...EMPTY_RESPONSES },
  );

  const prompts: PromptConfig[] = [
    {
      id: 'impactMatters',
      questionKey: NARRATIVE_PROMPT_QUESTION_KEYS.impactMatters,
      Icon: Star,
      color: 'from-yellow-500 to-orange-500',
      placeholderKey: 'performanceSnapshot.prompts.impactMatters.placeholder',
    },
    {
      id: 'limitedImpact',
      questionKey: NARRATIVE_PROMPT_QUESTION_KEYS.limitedImpact,
      Icon: AlertCircle,
      color: 'from-red-500 to-pink-500',
      placeholderKey: 'performanceSnapshot.prompts.limitedImpact.placeholder',
    },
    {
      id: 'doMoreLess',
      questionKey: NARRATIVE_PROMPT_QUESTION_KEYS.doMoreLess,
      Icon: TrendingUp,
      color: 'from-blue-500 to-cyan-500',
      placeholderKey: 'performanceSnapshot.prompts.doMoreLess.placeholder',
    },
    {
      id: 'scopeExpansion',
      questionKey: NARRATIVE_PROMPT_QUESTION_KEYS.scopeExpansion,
      Icon: Maximize2,
      color: 'from-purple-500 to-indigo-500',
      placeholderKey: 'performanceSnapshot.prompts.scopeExpansion.placeholder',
    },
  ];

  const handleResponseChange = (promptId: string, value: string) => {
    if (readOnly) return;
    const updatedResponses = {
      ...responses,
      [promptId]: value,
    };
    setResponses(updatedResponses);
    onPromptsUpdate(updatedResponses);
  };

  const completionCount = Object.values(responses).filter(
    (r) => r.trim().length > 0,
  ).length;
  const completionPercentage = (completionCount / prompts.length) * 100;

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-card border border-border rounded-lg p-6">
        {/* CO-433 — the in-panel "Save Responses" button is GONE. It toasted "Responses saved
            successfully" for a call that only wrote to React state; nothing reached the server.
            Answers now flow straight to page state on every keystroke, the page autosaves them, and
            the single save indicator in the status bar is the only place that says so. */}
        <div>
          <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <MessageSquare size={24} />
            <span>{t('performanceSnapshot.prompts.title')}</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('performanceSnapshot.prompts.subtitle')}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              {t('performanceSnapshot.prompts.completion')}: {completionCount}{' '}
              {t('performanceSnapshot.prompts.of')} {prompts.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(completionPercentage)}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Prompt Cards */}
      <div className="space-y-6">
        {prompts.map((prompt) => (
          <div key={prompt.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-4 mb-4">
              <div
                className={`w-12 h-12 bg-gradient-to-br ${prompt.color} rounded-lg flex items-center justify-center flex-shrink-0`}
              >
                <prompt.Icon size={24} color="white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t(prompt.questionKey)}
                </h4>
                <textarea
                  value={responses[prompt.id] || ''}
                  onChange={(e) => handleResponseChange(prompt.id, e.target.value)}
                  placeholder={t(prompt.placeholderKey)}
                  readOnly={readOnly}
                  rows={6}
                  className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">
                    {responses[prompt.id]?.length || 0}{' '}
                    {t('performanceSnapshot.prompts.characters')}
                  </span>
                  {responses[prompt.id]?.trim().length > 0 && (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle2 size={14} />
                      <span className="text-xs font-medium">
                        {t('performanceSnapshot.prompts.completed')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tips Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Lightbulb size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">
              {t('performanceSnapshot.prompts.tips.title')}
            </h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>{t('performanceSnapshot.prompts.tips.tip1')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip2')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip3')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip4')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip5')}</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};

export default NarrativePromptsPanel;
